<template>
  <section class="tb-sync-layout">
    <header class="tb-sync-topbar">
      <div class="tb-sync-topbar-title">
        <span class="pulse-dot" :class="{ 'is-off': !isSyncTb }"></span>
        天波基础信息同步
      </div>
      <nav class="tb-sync-tabs">
        <button
          v-for="tab in tabList"
          :key="tab.key"
          type="button"
          class="tb-sync-tab"
          :class="{ 'is-active': activeTab === tab.key }"
          @click="onModuleSelect(tab.key)"
        >
          <i :class="tab.icon"></i>
          <span>{{ tab.label }}</span>
        </button>
      </nav>
    </header>

    <main class="tb-sync-main">
      <div class="query-layout">
        <div class="dept-filter-pane">
          <div class="dept-filter-header">
            <i class="el-icon-school"></i>
            <span>选择机构 / 学校</span>
          </div>
          <!-- 仅部门 Tab 可切换家校/内部；学生/家长/教师由树标题区分，不再重复提示。最后更新：2026-08-30 -->
          <div v-if="activeTab === 'dept'" class="dept-scope-bar">
            <button
              type="button"
              class="dept-scope-btn"
              :class="{ 'is-active': deptDirectoryScope === 'teaching' }"
              @click="setDeptDirectoryScope('teaching')"
            >家校通讯录</button>
            <button
              type="button"
              class="dept-scope-btn"
              :class="{ 'is-active': deptDirectoryScope === 'internal' }"
              @click="setDeptDirectoryScope('internal')"
            >内部通讯录</button>
          </div>
          <DepartmentPane
            :key="paneDirectoryScope"
            :directory-scope="paneDirectoryScope"
            :pane-title="paneTitle"
            :default-select-root="true"
            @tree-node-click="onTreeNodeClick"
          />
        </div>

        <section class="query-main">
          <!-- 统计、状态、操作收成一行。最后修改：2026-10-14 -->
          <div class="sync-head">
            <div class="sync-head-row">
              <div class="sync-metrics">
                <div
                  class="stat-card stat-emerald"
                  :class="{ 'stat-card-clickable': canClickStatCard('synced') }"
                  @click="openStatsDetail('synced')"
                >
                  <span class="stat-label">已同步</span>
                  <span class="stat-value">{{ activeStats.synced }}<span class="unit">条</span></span>
                </div>
                <div
                  class="stat-card stat-orange"
                  :class="{ 'stat-card-clickable': canClickStatCard('unsynced') }"
                  @click="openStatsDetail('unsynced')"
                >
                  <span class="stat-label">未同步</span>
                  <span class="stat-value">{{ activeStats.unsynced }}<span class="unit">条</span></span>
                </div>
                <div
                  class="stat-card stat-blue"
                  :class="{ 'stat-card-clickable': canClickStatCard('total') }"
                  @click="openStatsDetail('total')"
                >
                  <span class="stat-label">总数</span>
                  <span class="stat-value">{{ activeStats.total }}<span class="unit">条</span></span>
                </div>
              </div>
              <div class="status-strip">
                <div class="status-chip">
                  <span>当前机构</span>
                  <strong>{{ currentDepartmentName || '未选择' }}</strong>
                </div>
                <div class="status-chip" :class="isSyncTb ? 'chip-success' : 'chip-muted'">
                  <span>天波对接</span>
                  <strong>{{ isSyncTb ? '已启用' : '未启用' }}</strong>
                </div>
                <div v-if="schoolConfig" class="status-chip" :class="schoolConfig.EnableManualBasicSync ? 'chip-success' : 'chip-muted'">
                  <span>手工同步</span>
                  <strong>{{ schoolConfig.EnableManualBasicSync ? '已允许' : '已关闭' }}</strong>
                </div>
                <div v-if="schoolInfo && schoolInfo.TbThirdPartId" class="status-chip">
                  <span>天波组织 ID</span>
                  <strong>{{ schoolInfo.TbThirdPartId }}</strong>
                </div>
              </div>
              <div class="sync-head-tools">
                <el-button
                  v-if="activeTab === 'student' && isCurrentClassNode"
                  type="success"
                  plain
                  size="small"
                  icon="el-icon-sort"
                  :loading="changeClassLoading"
                  :disabled="!canTriggerChangeClass()"
                  @click="triggerChangeClass"
                >换到本班</el-button>
                <el-button size="small" plain icon="el-icon-question" @click="guideVisible = true">说明</el-button>
                <el-button
                  type="primary"
                  size="small"
                  icon="el-icon-connection"
                  :loading="syncLoading[activeTab]"
                  :disabled="!canTriggerSync(activeTab)"
                  @click="triggerSync(activeTab)"
                >同步{{ activeTabLabel }}</el-button>
                <el-button
                  v-if="hasClearLocalAction(activeTab)"
                  type="warning"
                  plain
                  size="small"
                  icon="el-icon-refresh-left"
                  :loading="clearLocalLoading[activeTab]"
                  :disabled="!canTriggerClearLocal(activeTab)"
                  @click="triggerClearLocal(activeTab)"
                >清除本地ID</el-button>
                <el-button
                  v-if="hasTbDeleteAction(activeTab)"
                  type="danger"
                  plain
                  size="small"
                  icon="el-icon-delete"
                  :loading="deleteLoading[activeTab]"
                  :disabled="!canTriggerDelete(activeTab)"
                  @click="triggerDelete(activeTab)"
                >从天波删除</el-button>
                <el-button size="small" plain icon="el-icon-refresh" title="刷新统计" @click="loadStats(activeTab, true)"></el-button>
              </div>
            </div>
            <div v-if="!currentDepartmentId" class="sync-head-tip">
              <i class="el-icon-info"></i>
              <span>请先从左侧选择机构或学校节点</span>
            </div>
            <div v-else-if="!isSyncTb" class="sync-head-tip is-warning">
              <i class="el-icon-warning-outline"></i>
              <span>当前学校未启用天波对接，请在机构管理中开启「启用天波对接」。</span>
            </div>
            <div v-else-if="blockReason[activeTab] || deleteBlockReason[activeTab] || activeStats.tip" class="sync-head-tip">
              <i class="el-icon-info"></i>
              <span>{{ blockReason[activeTab] || deleteBlockReason[activeTab] || activeStats.tip }}</span>
            </div>
          </div>

          <el-card shadow="never" class="step-card branch-card log-card">
            <div slot="header" class="card-header-styled">
              <i class="el-icon-tickets"></i>
              <span>同步记录</span>
            </div>
            <TbSyncLogPanel
              ref="logPanel"
              :key="`${activeTab}-${currentDepartmentId || 0}`"
              :sync-type="activeTab"
              :root-department-id="currentDepartmentId"
              @history-loaded="(log) => onHistoryLoaded(activeTab, log)"
              @sync-completed="onSyncCompleted(activeTab)"
            />
          </el-card>
        </section>
      </div>
    </main>

    <el-dialog
      :visible.sync="guideVisible"
      title="天波同步说明"
      width="560px"
      append-to-body
      custom-class="sync-guide-dialog"
    >
      <div class="guide-block">
        <span class="desc-badge badge-primary">推荐顺序</span>
        <p class="guide-text">① 同步部门架构（家校 + 内部）→ ② 学生 → ③ 教师 → ④ 家长</p>
      </div>
      <div class="guide-block">
        <span class="desc-badge badge-warning">当前页签</span>
        <p v-if="activeTab === 'dept'" class="guide-text">左侧可切换家校 / 内部两套组织；将同步当前节点、未同步的上级链路及子部门。</p>
        <p v-else-if="activeTab === 'student'" class="guide-text">使用家校通讯录；请确认家校部门架构已同步，否则可能大量跳过。选中具体班级后可点「换到本班」：只把已有天波人员 ID 的学生换到该班的天波部门，不改本地班级。</p>
        <p v-else-if="activeTab === 'teacher'" class="guide-text">使用内部通讯录；请确认内部部门架构已同步。</p>
        <p v-else class="guide-text">使用家校通讯录；请确认学生已同步；无手机号家长将跳过。</p>
      </div>
      <div class="guide-block">
        <span class="desc-badge badge-warning">重建建议</span>
        <p class="guide-text">天波重建后先「清除本地ID」（部门→人员），再按部门→学生→教师→家长同步。失败明细里的说明含天波 code/message。</p>
      </div>
    </el-dialog>

    <el-dialog
      :title="statsDetailTitle"
      :visible.sync="statsDetailVisible"
      width="960px"
      top="8vh"
      append-to-body
      custom-class="tb-sync-detail-dialog tb-sync-stats-detail-dialog"
      @closed="onStatsDetailClosed"
      @opened="layoutStatsDetailTable"
    >
      <div class="stats-detail-meta">
        <span><i class="el-icon-location-outline"></i> 当前机构：{{ currentDepartmentName || '' }}</span>
        <span v-if="statsDetailTip" class="stats-detail-tip"><i class="el-icon-info"></i> {{ statsDetailTip }}</span>
      </div>
      <div class="stats-detail-toolbar">
        <el-input
          v-model="statsDetailKeyword"
          size="small"
          clearable
          :placeholder="statsDetailSearchPlaceholder"
          prefix-icon="el-icon-search"
          class="stats-detail-search-input"
          @keyup.enter.native="onStatsDetailSearch"
          @clear="onStatsDetailSearch"
        />
        <el-button size="small" type="primary" plain icon="el-icon-search" @click="onStatsDetailSearch">搜索</el-button>
      </div>
      <div class="stats-detail-table-wrap">
        <el-table
          ref="statsDetailTable"
          :data="statsDetailList"
          border
          size="small"
          :height="statsDetailTableHeight"
          v-loading="statsDetailLoading"
          class="custom-table dialog-table stats-detail-table"
        >
        <el-table-column
          v-for="col in statsDetailColumns"
          :key="col.prop"
          :prop="col.prop"
          :label="col.label"
          :width="col.width"
          :min-width="col.minWidth"
          :align="col.align"
          :header-align="col.headerAlign || col.align"
          :show-overflow-tooltip="shouldStatsDetailOverflowTooltip(col)"
        >
          <template slot-scope="scope">
            <template v-if="col.columnType === 'syncStatus'">
              <el-tag :type="scope.row.isSynced ? 'success' : 'warning'" size="mini">
                {{ scope.row.isSynced ? '已同步' : '未同步' }}
              </el-tag>
            </template>
            <template v-else>
              <span
                v-if="isTbSyncTbIdProp(col.prop) && formatTbSyncTbIdDisplay(scope.row[col.prop])"
                class="detail-id-cell detail-id-cell-ellipsis"
              >
                {{ formatTbSyncTbIdDisplay(scope.row[col.prop]) }}
              </span>
              <span
                v-else-if="isTbSyncEntityIdProp(col.prop) && formatTbSyncCellDisplay(scope.row[col.prop])"
                class="detail-id-cell detail-id-cell-ellipsis"
              >
                {{ formatTbSyncCellDisplay(scope.row[col.prop]) }}
              </span>
              <span v-else-if="!isTbSyncTbIdProp(col.prop) && !isTbSyncEntityIdProp(col.prop)">
                {{ formatTbSyncCellDisplay(scope.row[col.prop]) }}
              </span>
            </template>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" align="center">
          <template slot-scope="scope">
            <div class="table-op-stacked stats-detail-op-cell">
              <div class="stats-detail-op-row">
                <el-button
                  type="text"
                  size="small"
                  class="link-btn"
                  :loading="statsDetailItemLoading === getStatsDetailItemKey(scope.row)"
                  :disabled="!canStatsDetailRowAction(scope.row, 'sync')"
                  @click="statsDetailSyncRow(scope.row)"
                >同步</el-button>
                <el-button
                  v-if="scope.row.isSynced && !scope.row.isSchoolRoot"
                  type="text"
                  size="small"
                  class="link-btn link-btn-warning"
                  :loading="statsDetailItemLoading === getStatsDetailItemKey(scope.row)"
                  :disabled="!canStatsDetailRowAction(scope.row, 'clearLocal')"
                  @click="statsDetailClearLocalRow(scope.row)"
                >清除本地ID</el-button>
              </div>
              <div
                v-if="scope.row.isSynced && !scope.row.isSchoolRoot"
                class="stats-detail-op-row"
              >
                <el-button
                  type="text"
                  size="small"
                  class="link-btn link-btn-danger"
                  :loading="statsDetailItemLoading === getStatsDetailItemKey(scope.row)"
                  :disabled="!canStatsDetailRowAction(scope.row, 'delete')"
                  @click="statsDetailDeleteRow(scope.row)"
                >从天波删除</el-button>
              </div>
            </div>
          </template>
        </el-table-column>
        </el-table>
      </div>
      <div class="pagination-bar">
        <el-pagination
          small
          layout="total, prev, pager, next"
          :total="statsDetailTotal"
          :page-size="statsDetailPageSize"
          :current-page.sync="statsDetailPage"
          @current-change="fetchStatsDetailPage"
        />
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="statsDetailVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import { treeNodeDeptId } from '../../utils/deptCascaderProps';
import TbSyncLogPanel from '../../components/TbSync/TbSyncLogPanel';
import { getDepartmentType, getTbDeptStats, getTbStudentStats, getTbTeacherStats, getTbParentStats, changeTbStudentsToCurrentClass } from '../../api/modules/department';
import { getSchoolTbConfig } from '../../api/modules/openPlatform';
import {
  tbSyncApiMap,
  calcCooldownRemainSeconds,
  formatCooldownText,
  normalizeTbSyncLogPayload,
  isTbSyncEntityIdProp,
  isTbSyncTbIdProp,
  formatTbSyncTbIdDisplay,
  formatTbSyncCellDisplay,
} from '../../components/TbSync/tbSyncApiMap';

/**
 * 天波基础信息同步统一页面
 * 路由建议：/User/TbBasicSync
 * 创建于 2026-06-13
 * 样式于 2026-06-13 对齐 OpsMaintenanceHub
 */
export default {
  name: 'TbBasicSync',
  components: { DepartmentPane, TbSyncLogPanel },
  data() {
    return {
      activeTab: 'dept',
      guideVisible: false,
      /** 部门 Tab 下的通讯录切换：teaching 家校 / internal 内部。最后更新：2026-08-30 */
      deptDirectoryScope: 'teaching',
      currentDepartment: null,
      currentDepartmentId: null,
      currentDepartmentName: '',
      schoolInfo: null,
      schoolConfig: null,
      schoolConfigLoading: false,
      isSyncTb: false,
      syncLoading: { dept: false, student: false, teacher: false, parent: false },
      changeClassLoading: false,
      deleteLoading: { student: false, teacher: false, parent: false },
      clearLocalLoading: { dept: false, student: false, teacher: false, parent: false },
      blockReason: { dept: '', student: '', teacher: '', parent: '' },
      deleteBlockReason: { student: '', teacher: '', parent: '' },
      cooldownTimer: null,
      cooldownRemain: { dept: 0, student: 0, teacher: 0, parent: 0 },
      stats: {
        dept: { total: 0, synced: 0, unsynced: 0, tip: '' },
        student: { total: 0, synced: 0, unsynced: 0, tip: '', pendingDeleteCount: 0 },
        teacher: { total: 0, synced: 0, unsynced: 0, tip: '', pendingDeleteCount: 0 },
        parent: { total: 0, synced: 0, unsynced: 0, tip: '', pendingDeleteCount: 0 },
      },
      tabList: [
        { key: 'dept', label: '部门架构', icon: 'el-icon-office-building' },
        { key: 'student', label: '学生', icon: 'el-icon-user' },
        { key: 'teacher', label: '教师', icon: 'el-icon-s-custom' },
        { key: 'parent', label: '家长', icon: 'el-icon-phone-outline' },
      ],
      statsApiMap: {
        dept: getTbDeptStats,
        student: getTbStudentStats,
        teacher: getTbTeacherStats,
        parent: getTbParentStats,
      },
      statsDetailVisible: false,
      statsDetailTabKey: 'student',
      statsDetailFilterType: 'total',
      statsDetailList: [],
      statsDetailTotal: 0,
      statsDetailPage: 1,
      statsDetailPageSize: 10,
      statsDetailLoading: false,
      statsDetailKeyword: '',
      statsDetailItemLoading: null,
      statFilterMap: { synced: 1, unsynced: 2, total: 0 },
      statLabelMap: { synced: '已同步', unsynced: '未同步', total: '全部' },
    };
  },
  computed: {
    activeTabLabel() {
      const tab = this.tabList.find((t) => t.key === this.activeTab);
      return tab ? tab.label : '';
    },
    /** 当前左侧节点是否为班级。最后更新：2026-10-15 */
    isCurrentClassNode() {
      const node = this.currentDepartment;
      const type = String((node && (node.DeptOrgType || node.deptOrgType)) || '').toLowerCase();
      return type === 'class';
    },
    /**
     * 左侧树通讯录范围：学生/家长→家校，教师→内部，部门→可切换。最后更新：2026-08-30
     */
    paneDirectoryScope() {
      if (this.activeTab === 'teacher') return 'internal';
      if (this.activeTab === 'student' || this.activeTab === 'parent') return 'teaching';
      return this.deptDirectoryScope === 'internal' ? 'internal' : 'teaching';
    },
    paneTitle() {
      return this.paneDirectoryScope === 'internal' ? '内部通讯录' : '家校通讯录';
    },
    /** 部门统计/同步用的通讯录类型：0 内部、1 家校 */
    deptStatsBigType() {
      return this.paneDirectoryScope === 'internal' ? 0 : 1;
    },
    activeStats() {
      return this.stats[this.activeTab] || { total: 0, synced: 0, unsynced: 0, tip: '' };
    },
    statsDetailConfig() {
      return tbSyncApiMap[this.statsDetailTabKey] || tbSyncApiMap.student;
    },
    statsDetailColumns() {
      return (this.statsDetailConfig && this.statsDetailConfig.statsDetailColumns) || [];
    },
    statsDetailTitle() {
      const tab = this.tabList.find((t) => t.key === this.statsDetailTabKey);
      const tabLabel = tab ? tab.label : '';
      const filterLabel = this.statLabelMap[this.statsDetailFilterType] || '';
      return `${tabLabel} · ${filterLabel}明细`;
    },
    statsDetailTip() {
      if (this.statsDetailTabKey !== 'dept') return '';
      return (this.stats.dept && this.stats.dept.tip) || '';
    },
    statsDetailSearchPlaceholder() {
      const cfg = this.statsDetailConfig;
      return (cfg && cfg.statsDetailSearchPlaceholder) || '关键词';
    },
    /**
     * 表格高度：按行数估算，并限制在视口内，避免 dialog body 再出滚动条。
     * 最后更新：2026-10-21
     */
    statsDetailTableHeight() {
      const headerHeight = 40;
      const rowHeight = 48;
      const ideal = headerHeight + this.statsDetailPageSize * rowHeight;
      // meta + 搜索 + 分页 + 内边距约 200，dialog 最大约 72vh
      const maxByViewport = Math.floor(window.innerHeight * 0.72) - 200;
      return Math.max(280, Math.min(ideal, maxByViewport));
    },
  },
  destroyed() {
    this.stopCooldownTimer();
  },
  methods: {
    isTbSyncEntityIdProp,
    isTbSyncTbIdProp,
    formatTbSyncTbIdDisplay,
    formatTbSyncCellDisplay,
    /** 统计明细：ID 列自带省略号，勿再开 overflow tooltip，避免空值悬停出现黑点气泡。 */
    shouldStatsDetailOverflowTooltip(col) {
      if (!col || col.columnType === 'syncStatus') return false;
      if (isTbSyncTbIdProp(col.prop) || isTbSyncEntityIdProp(col.prop)) return false;
      return true;
    },
    onModuleSelect(key) {
      if (this.activeTab === key) return;
      const prevScope = this.paneDirectoryScope;
      this.activeTab = key;
      // 切换 Tab 导致通讯录范围变化时清空当前选中，避免跨树误用节点。最后更新：2026-08-30
      this.$nextTick(() => {
        if (this.paneDirectoryScope !== prevScope) {
          this.clearCurrentDepartment();
        }
        this.onTabChange();
      });
    },
    /** 部门 Tab 内切换家校/内部组织树 */
    setDeptDirectoryScope(scope) {
      if (scope !== 'teaching' && scope !== 'internal') return;
      if (this.deptDirectoryScope === scope) return;
      this.deptDirectoryScope = scope;
      this.clearCurrentDepartment();
      // 先清统计，待树默认选中学校后再按新 BigType 重载。最后更新：2026-08-30
      this.stats.dept = { total: 0, synced: 0, unsynced: 0, tip: '', pendingDeleteCount: 0 };
    },
    clearCurrentDepartment() {
      this.currentDepartment = null;
      this.currentDepartmentId = null;
      this.currentDepartmentName = '';
      this.schoolInfo = null;
      this.schoolConfig = null;
      this.isSyncTb = false;
    },
    onTreeNodeClick(data) {
      this.currentDepartment = data;
      this.currentDepartmentId = treeNodeDeptId(data) || null;
      this.currentDepartmentName = (data && (data.label || data.name)) || '';
      this.loadDepartmentInfo();
    },
    loadDepartmentInfo() {
      if (!this.currentDepartmentId) {
        this.schoolInfo = null;
        this.schoolConfig = null;
        this.isSyncTb = false;
        return;
      }
      getDepartmentType({ id: this.currentDepartmentId }).then((res) => {
        if (res.data.success && res.data.response) {
          this.schoolInfo = res.data.response;
          this.isSyncTb = res.data.response.IsSyncTb === '1';
          this.loadSchoolConfig();
        } else {
          this.schoolInfo = null;
          this.schoolConfig = null;
          this.isSyncTb = false;
          this.refreshAll();
        }
      }).catch(() => {
        this.schoolInfo = null;
        this.schoolConfig = null;
        this.isSyncTb = false;
        this.refreshAll();
      });
    },
    /** 加载学校层手工同步与资源细项。 */
    loadSchoolConfig() {
      if (!this.schoolInfo || !this.schoolInfo.Id) {
        this.schoolConfig = null;
        this.refreshAll();
        return;
      }
      this.schoolConfigLoading = true;
      getSchoolTbConfig(this.schoolInfo.Id).then((res) => {
        this.schoolConfig = res.data && res.data.success ? (res.data.response || null) : null;
        if (!this.schoolConfig) this.$message.error((res.data && res.data.msg) || '读取学校同步配置失败');
      }).catch(() => {
        this.schoolConfig = null;
        this.$message.error('读取学校同步配置失败');
      }).finally(() => {
        this.schoolConfigLoading = false;
        this.refreshAll();
      });
    },
    refreshAll() {
      this.tabList.forEach((t) => {
        this.loadStats(t.key);
      });
      this.reloadLogPanel(this.activeTab);
      this.loadCooldownForAllTabs();
      this.startCooldownTimer();
    },
    onTabChange() {
      this.reloadLogPanel(this.activeTab);
      this.loadStats(this.activeTab);
    },
    reloadLogPanel(tabKey) {
      if (tabKey && tabKey !== this.activeTab) return;
      this.$nextTick(() => {
        const panel = this.$refs.logPanel;
        if (panel && panel.resetAndLoad) {
          panel.resetAndLoad();
        }
      });
    },
    /** 三 Tab 冷却期：各拉一条最新同步记录 */
    loadCooldownForAllTabs() {
      if (!this.currentDepartmentId) return;
      this.tabList.forEach((t) => {
        const config = tbSyncApiMap[t.key];
        if (!config || !config.getLogPage) return;
        config.getLogPage({
          rootDepartmentId: this.currentDepartmentId,
          pageIndex: 1,
          pageSize: 1,
        }).then((res) => {
          if (res.data.success && res.data.response) {
            const p = res.data.response;
            const rows = p.data != null ? p.data : p.Data;
            const latest = rows && rows.length ? normalizeTbSyncLogPayload(rows[0]) : null;
            this.onHistoryLoaded(t.key, latest);
          }
        });
      });
    },
    /** 加载指定 Tab 统计数据；manualRefresh 为 true 时展示操作反馈 */
    loadStats(tabKey, manualRefresh = false) {
      if (!this.currentDepartmentId) {
        if (manualRefresh) {
          this.$message.info('请先选择左侧部门');
        }
        return;
      }
      const api = this.statsApiMap[tabKey];
      if (!api) return;
      const params = { departmentId: this.currentDepartmentId };
      // 部门架构按当前通讯录范围过滤统计。最后更新：2026-08-30
      if (tabKey === 'dept') {
        params.bigType = this.deptStatsBigType;
      }
      api(params).then((res) => {
        if (res.data.success && res.data.response) {
          const r = res.data.response;
          this.stats[tabKey] = {
            total: r.total != null ? r.total : r.Total,
            synced: r.synced != null ? r.synced : r.Synced,
            unsynced: r.unsynced != null ? r.unsynced : r.Unsynced,
            tip: r.tip || r.Tip || '',
            pendingDeleteCount: r.pendingDeleteCount != null ? r.pendingDeleteCount : (r.PendingDeleteCount || 0),
          };
          this.updateDeleteBlockReason(tabKey);
          if (manualRefresh) {
            const tab = this.tabList.find((t) => t.key === tabKey);
            const tabLabel = tab ? tab.label : '';
            this.$message.success(`${tabLabel}统计已刷新`);
          }
        } else if (manualRefresh) {
          this.$message.error(res.data.msg || '刷新统计失败');
        }
      }).catch(() => {
        if (manualRefresh) {
          this.$message.error('刷新统计失败');
        }
      });
    },
    onHistoryLoaded(tabKey, latestLog) {
      const remain = calcCooldownRemainSeconds(latestLog);
      this.cooldownRemain[tabKey] = remain;
      this.updateBlockReason(tabKey);
    },
    updateBlockReason(tabKey) {
      if (!this.currentDepartmentId) {
        this.blockReason[tabKey] = '请先选择左侧部门';
        return;
      }
      if (!this.isSyncTb) {
        this.blockReason[tabKey] = '当前机构未启用天波同步';
        return;
      }
      if (this.schoolConfigLoading) {
        this.blockReason[tabKey] = '正在读取学校同步配置';
        return;
      }
      if (!this.schoolConfig) {
        this.blockReason[tabKey] = '学校同步配置读取失败，请刷新后重试';
        return;
      }
      if (!this.schoolConfig.EnableManualBasicSync) {
        this.blockReason[tabKey] = '学校配置已关闭手工基础同步';
        return;
      }
      const resourceKeyMap = {
        dept: 'AllowDept',
        student: 'AllowStudent',
        teacher: 'AllowStaff',
        parent: 'AllowParent',
      };
      const resourceKey = resourceKeyMap[tabKey];
      if (resourceKey && this.schoolConfig[resourceKey] === false) {
        const tab = this.tabList.find((item) => item.key === tabKey);
        this.blockReason[tabKey] = `学校配置未开放${tab ? tab.label : ''}资源`;
        return;
      }
      if (tabKey === 'dept') {
        if (!this.schoolInfo || !this.schoolInfo.TbThirdPartId || Number(this.schoolInfo.TbThirdPartId) === 0) {
          this.blockReason[tabKey] = '请先同步机构，再同步子部门';
          return;
        }
      }
      const remain = this.cooldownRemain[tabKey];
      if (remain === -1) {
        this.blockReason[tabKey] = '当前存在进行中的同步任务，请稍后';
        return;
      }
      if (remain > 0) {
        this.blockReason[tabKey] = formatCooldownText(remain);
        return;
      }
      this.blockReason[tabKey] = '';
      this.updateDeleteBlockReason(tabKey);
    },
    updateDeleteBlockReason(tabKey) {
      if (!this.hasTbDeleteAction(tabKey)) {
        this.deleteBlockReason[tabKey] = '';
        return;
      }
      if (!this.currentDepartmentId || !this.isSyncTb) {
        this.deleteBlockReason[tabKey] = '';
        return;
      }
      if (this.blockReason[tabKey]) {
        this.deleteBlockReason[tabKey] = this.blockReason[tabKey];
        return;
      }
      const pending = (this.stats[tabKey] && this.stats[tabKey].pendingDeleteCount) || 0;
      if (pending <= 0) {
        this.deleteBlockReason[tabKey] = '当前范围内无已同步数据可删除';
        return;
      }
      this.deleteBlockReason[tabKey] = '';
    },
    hasTbDeleteAction(tabKey) {
      const config = tbSyncApiMap[tabKey];
      return !!(config && config.triggerDelete);
    },
    canTriggerSync(tabKey) {
      return !this.blockReason[tabKey]
        && this.isSyncTb
        && !!this.currentDepartmentId
        && !this.syncLoading[tabKey]
        && !this.deleteLoading[tabKey]
        && !this.clearLocalLoading[tabKey]
        && !this.changeClassLoading;
    },
    /** 学生页签、且当前选中的是班级时，才允许换到本班。最后更新：2026-10-15 */
    canTriggerChangeClass() {
      return this.activeTab === 'student'
        && this.isCurrentClassNode
        && this.canTriggerSync('student');
    },
    triggerChangeClass() {
      if (!this.canTriggerChangeClass()) return;
      const deptName = this.currentDepartmentName || '当前班级';
      this.$confirm(
        `将【${deptName}】中已有天波人员 ID 的学生，换到这个班在天波上的部门。本地班级不会改变。还没有天波人员 ID 的学生会跳过。`,
        '换到本班',
        { type: 'warning' }
      ).then(() => {
        this.changeClassLoading = true;
        changeTbStudentsToCurrentClass({ Id: this.currentDepartmentId }).then((res) => {
          this.changeClassLoading = false;
          if (res.data.success) {
            this.$message.success(res.data.msg || '已提交换班任务');
            this.reloadLogPanel('student');
            this.loadStats('student');
          } else {
            this.$message.error(res.data.msg || '换班提交失败');
          }
        }).catch(() => {
          this.changeClassLoading = false;
          this.$message.error('换班请求失败');
        });
      }).catch(() => {});
    },
    canTriggerDelete(tabKey) {
      return this.canTriggerSync(tabKey)
        && this.hasTbDeleteAction(tabKey)
        && ((this.stats[tabKey] && this.stats[tabKey].pendingDeleteCount) || 0) > 0;
    },
    hasClearLocalAction(tabKey) {
      const config = tbSyncApiMap[tabKey];
      return !!(config && config.triggerClearLocal);
    },
    canTriggerClearLocal(tabKey) {
      return this.isSyncTb
        && !!this.currentDepartmentId
        && this.hasClearLocalAction(tabKey)
        && !this.syncLoading[tabKey]
        && !this.deleteLoading[tabKey]
        && !this.clearLocalLoading[tabKey]
        && ((this.stats[tabKey] && this.stats[tabKey].synced) || 0) > 0;
    },
    triggerSync(tabKey) {
      if (!this.canTriggerSync(tabKey)) return;
      const config = tbSyncApiMap[tabKey];
      const deptName = this.currentDepartmentName || '';
      this.$confirm(config.confirmMessage.replace(/\{name\}/g, deptName), '提示', {
        type: 'warning',
        dangerouslyUseHTMLString: true,
      }).then(() => {
        this.syncLoading[tabKey] = true;
        const syncPayload = { Id: this.currentDepartmentId };
        if (tabKey === 'dept') syncPayload.BigType = this.deptStatsBigType;
        config.triggerSync(syncPayload).then((res) => {
          this.syncLoading[tabKey] = false;
          if (res.data.success) {
            this.$message.success(res.data.msg || '已提交同步任务');
            this.reloadLogPanel(tabKey);
            this.loadStats(tabKey);
          } else {
            this.$message.error(res.data.msg || '同步提交失败');
          }
        }).catch(() => {
          this.syncLoading[tabKey] = false;
          this.$message.error('同步请求失败');
        });
      }).catch(() => {});
    },
    triggerDelete(tabKey) {
      if (!this.canTriggerDelete(tabKey)) {
        if (this.deleteBlockReason[tabKey]) {
          this.$message.warning(this.deleteBlockReason[tabKey]);
        }
        return;
      }
      const config = tbSyncApiMap[tabKey];
      if (!config || !config.triggerDelete) return;
      const deptName = this.currentDepartmentName || '';
      const message = (config.deleteConfirmMessage || '').replace(/\{name\}/g, deptName);
      this.$confirm(message, '从天波删除', {
        type: 'warning',
        dangerouslyUseHTMLString: true,
      }).then(() => {
        this.deleteLoading[tabKey] = true;
        config.triggerDelete({ Id: this.currentDepartmentId }).then((res) => {
          this.deleteLoading[tabKey] = false;
          if (res.data.success) {
            this.$message.success(res.data.msg || '已提交删除任务');
            this.reloadLogPanel(tabKey);
            this.loadStats(tabKey);
          } else {
            this.$message.error(res.data.msg || '删除提交失败');
          }
        }).catch(() => {
          this.deleteLoading[tabKey] = false;
          this.$message.error('删除请求失败');
        });
      }).catch(() => {});
    },
    /**
     * 仅清除本地天波ID/映射，不调用厂家删除。创建于 2026-08-25
     */
    triggerClearLocal(tabKey) {
      if (!this.canTriggerClearLocal(tabKey)) {
        this.$message.warning('当前无可清除的已同步数据，或任务进行中');
        return;
      }
      const config = tbSyncApiMap[tabKey];
      if (!config || !config.triggerClearLocal) return;
      const deptName = this.currentDepartmentName || '';
      const message = (config.clearLocalConfirmMessage || '').replace(/\{name\}/g, deptName);
      this.$confirm(message, '仅清除本地天波ID', {
        type: 'warning',
        dangerouslyUseHTMLString: true,
      }).then(() => {
        this.clearLocalLoading[tabKey] = true;
        const clearPayload = { Id: this.currentDepartmentId };
        if (tabKey === 'dept') clearPayload.BigType = this.deptStatsBigType;
        config.triggerClearLocal(clearPayload).then((res) => {
          this.clearLocalLoading[tabKey] = false;
          if (res.data.success) {
            const count = (res.data.response && res.data.response.clearedCount) || 0;
            this.$message.success(res.data.msg || `已清除 ${count} 条`);
            this.loadStats(tabKey);
          } else {
            this.$message.error(res.data.msg || '清除失败');
          }
        }).catch(() => {
          this.clearLocalLoading[tabKey] = false;
          this.$message.error('清除请求失败');
        });
      }).catch(() => {});
    },
    onSyncCompleted(tabKey) {
      this.loadStats(tabKey);
      this.reloadLogPanel(tabKey);
    },
    startCooldownTimer() {
      this.stopCooldownTimer();
      this.cooldownTimer = setInterval(() => {
        this.tabList.forEach((t) => {
          if (this.cooldownRemain[t.key] > 0) {
            this.cooldownRemain[t.key] -= 1;
          }
          this.updateBlockReason(t.key);
        });
      }, 1000);
    },
    stopCooldownTimer() {
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer);
        this.cooldownTimer = null;
      }
    },
    /** 统计卡片是否可点击查看明细 */
    canClickStatCard(type) {
      if (!this.currentDepartmentId) return false;
      const countMap = {
        synced: this.activeStats.synced,
        unsynced: this.activeStats.unsynced,
        total: this.activeStats.total,
      };
      return (countMap[type] || 0) > 0;
    },
    /** 打开统计明细分页弹窗 */
    openStatsDetail(type) {
      if (!this.canClickStatCard(type)) {
        if (!this.currentDepartmentId) {
          this.$message.info('请先选择左侧部门');
        } else {
          this.$message.info('暂无相关数据');
        }
        return;
      }
      this.statsDetailTabKey = this.activeTab;
      this.statsDetailFilterType = type;
      this.statsDetailPage = 1;
      this.statsDetailKeyword = '';
      this.statsDetailVisible = true;
      this.fetchStatsDetailPage();
    },
    onStatsDetailSearch() {
      this.statsDetailPage = 1;
      this.fetchStatsDetailPage();
    },
    fetchStatsDetailPage() {
      const config = this.statsDetailConfig;
      if (!config || !config.getStatsDetailPage || !this.currentDepartmentId) return;
      this.statsDetailLoading = true;
      const keyword = (this.statsDetailKeyword || '').trim();
      const detailParams = {
        departmentId: this.currentDepartmentId,
        pageIndex: this.statsDetailPage,
        pageSize: this.statsDetailPageSize,
        syncFilter: this.statFilterMap[this.statsDetailFilterType] != null
          ? this.statFilterMap[this.statsDetailFilterType]
          : 0,
        keyword: keyword || undefined,
      };
      // 部门明细与统计卡片同一通讯录范围。最后更新：2026-08-30
      if (this.activeTab === 'dept') {
        detailParams.bigType = this.deptStatsBigType;
      }
      config.getStatsDetailPage(detailParams).then((res) => {
        this.statsDetailLoading = false;
        if (res.data.success && res.data.response) {
          const p = res.data.response;
          const rows = p.data != null ? p.data : p.Data;
          this.statsDetailTotal = p.dataCount != null ? p.dataCount : p.DataCount;
          const normalize = config.normalizeStatsDetail || ((r) => r);
          this.statsDetailList = (rows || []).map((r) => normalize(r));
          this.layoutStatsDetailTable();
        }
      }).catch(() => {
        this.statsDetailLoading = false;
        this.$message.error('加载明细失败');
      });
    },
    /** 修正弹窗表格表头/表体列宽错位（Element UI 在 dialog + 滚动时常见） */
    layoutStatsDetailTable() {
      const runLayout = () => {
        const table = this.$refs.statsDetailTable;
        if (table && typeof table.doLayout === 'function') {
          table.doLayout();
        }
      };
      this.$nextTick(runLayout);
      setTimeout(runLayout, 320);
    },
    onStatsDetailClosed() {
      this.statsDetailList = [];
      this.statsDetailTotal = 0;
      this.statsDetailPage = 1;
      this.statsDetailKeyword = '';
      this.statsDetailItemLoading = null;
    },
    getStatsDetailItemKey(row) {
      const config = this.statsDetailConfig;
      if (!config || !config.getDetailItemId || !row) return '';
      const id = config.getDetailItemId(row);
      return id != null ? String(id) : '';
    },
    /** 单条操作仅拦截高峰/进行中/前置条件，不拦批量冷却。最后更新：2026-10-21 */
    getStatsDetailItemBlockReason(tabKey) {
      const blockReason = this.blockReason[tabKey] || '';
      if (!blockReason) return '';
      if (blockReason.includes('高峰')) return blockReason;
      if (blockReason.includes('进行中') || blockReason.includes('请稍候') || blockReason.includes('请稍后')) return blockReason;
      if (blockReason.includes('请先') || blockReason.includes('请选择')) return blockReason;
      if (blockReason.includes('未启用') || blockReason.includes('未允许')) return blockReason;
      return '';
    },
    canStatsDetailRowAction(row, action) {
      const tabKey = this.statsDetailTabKey;
      if (!row || !this.isSyncTb || !this.currentDepartmentId) return false;
      if (this.syncLoading[tabKey] || this.deleteLoading[tabKey] || this.clearLocalLoading[tabKey]) return false;
      if (this.getStatsDetailItemBlockReason(tabKey)) return false;
      if (action === 'sync') return true;
      if (action === 'clearLocal' || action === 'delete') {
        if (!row.isSynced || row.isSchoolRoot) return false;
        return true;
      }
      return false;
    },
    statsDetailSyncRow(row) {
      const tabKey = this.statsDetailTabKey;
      if (!this.canStatsDetailRowAction(row, 'sync')) {
        const reason = this.getStatsDetailItemBlockReason(tabKey) || this.blockReason[tabKey];
        if (reason) this.$message.warning(reason);
        return;
      }
      const config = this.statsDetailConfig;
      if (!config || !config.syncItem) return;
      const itemId = config.getDetailItemId(row);
      if (!itemId) return;
      this.$confirm('确认将该条数据同步到天波吗？', '快速同步', { type: 'warning' })
        .then(() => {
          const loadingKey = this.getStatsDetailItemKey(row);
          this.statsDetailItemLoading = loadingKey;
          config.syncItem({ Id: itemId }).then((res) => {
            this.statsDetailItemLoading = null;
            if (res.data.success) {
              this.$message.success(res.data.msg || '已提交同步任务');
              this.fetchStatsDetailPage();
              this.loadStats(tabKey);
              this.reloadLogPanel(tabKey);
            } else {
              this.$message.error(res.data.msg || '同步提交失败');
            }
          }).catch(() => {
            this.statsDetailItemLoading = null;
            this.$message.error('同步请求失败');
          });
        }).catch(() => {});
    },
    statsDetailClearLocalRow(row) {
      const tabKey = this.statsDetailTabKey;
      if (!this.canStatsDetailRowAction(row, 'clearLocal')) {
        if (row && row.isSchoolRoot) {
          this.$message.warning('学校根组织ID不允许清除');
          return;
        }
        const reason = this.getStatsDetailItemBlockReason(tabKey) || this.blockReason[tabKey];
        if (reason) this.$message.warning(reason);
        return;
      }
      const config = this.statsDetailConfig;
      if (!config || !config.clearLocalItem) return;
      const itemId = config.getDetailItemId(row);
      if (!itemId) return;
      const message = config.clearLocalItemConfirmMessage
        || '将清除该条<strong>本地天波ID/映射</strong>（<strong>不会</strong>调用天波删除）。<br/><span style="color:#b45309">请先确认已在天波后台手动删除对应数据</span>，否则再同步会按新增处理，天波侧可能出现重复数据。是否继续？';
      this.$confirm(message, '仅清除本地天波ID', {
        type: 'warning',
        dangerouslyUseHTMLString: true,
      }).then(() => {
        const loadingKey = this.getStatsDetailItemKey(row);
        this.statsDetailItemLoading = loadingKey;
        config.clearLocalItem({ Id: itemId }).then((res) => {
          this.statsDetailItemLoading = null;
          if (res.data.success) {
            this.$message.success(res.data.msg || '已清除');
            this.fetchStatsDetailPage();
            this.loadStats(tabKey);
          } else {
            this.$message.error(res.data.msg || '清除失败');
          }
        }).catch(() => {
          this.statsDetailItemLoading = null;
          this.$message.error('清除请求失败');
        });
      }).catch(() => {});
    },
    statsDetailDeleteRow(row) {
      const tabKey = this.statsDetailTabKey;
      if (!this.canStatsDetailRowAction(row, 'delete')) {
        if (row && row.isSchoolRoot) {
          this.$message.warning('学校根组织不允许从天波删除');
          return;
        }
        const reason = this.getStatsDetailItemBlockReason(tabKey) || this.blockReason[tabKey];
        if (reason) this.$message.warning(reason);
        return;
      }
      const config = this.statsDetailConfig;
      if (!config || !config.deleteItem) return;
      const itemId = config.getDetailItemId(row);
      if (!itemId) return;
      this.$confirm(
        '确认从天波删除该项吗？<br/>仅删除<strong>天波侧数据</strong>并清除<strong>本地天波ID/映射</strong>，本地业务数据保留。',
        '快速删除',
        { type: 'warning', dangerouslyUseHTMLString: true },
      ).then(() => {
        const loadingKey = this.getStatsDetailItemKey(row);
        this.statsDetailItemLoading = loadingKey;
        config.deleteItem({ Id: itemId }).then((res) => {
          this.statsDetailItemLoading = null;
          if (res.data.success) {
            this.$message.success(res.data.msg || '已提交删除任务');
            this.fetchStatsDetailPage();
            this.loadStats(tabKey);
            this.reloadLogPanel(tabKey);
          } else {
            this.$message.error(res.data.msg || '删除提交失败');
          }
        }).catch(() => {
          this.statsDetailItemLoading = null;
          this.$message.error('删除请求失败');
        });
      }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.tb-sync-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  background: #f8fafc;
  font-family: "PingFang SC", "Microsoft YaHei", "Noto Sans CJK SC", sans-serif;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.tb-sync-topbar {
  flex-shrink: 0;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 14px 24px 0;
}

.tb-sync-topbar-title {
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #1e1b4b 0%, #4338ca 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  background: #10b981;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  animation: pulse 2s infinite;
}

.pulse-dot.is-off {
  background: #94a3b8;
  box-shadow: none;
  animation: none;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

.tb-sync-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tb-sync-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-bottom: 3px solid transparent;
  border-radius: 8px 8px 0 0;
  background: transparent;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: -1px;
}

.tb-sync-tab i {
  font-size: 15px;
  color: #94a3b8;
}

.tb-sync-tab:hover {
  color: #4f46e5;
  background: #f8fafc;
}

.tb-sync-tab:hover i {
  color: #4f46e5;
}

.tb-sync-tab.is-active {
  color: #4f46e5;
  font-weight: 600;
  background: linear-gradient(180deg, rgba(79, 70, 229, 0.06) 0%, transparent 100%);
  border-bottom-color: #4f46e5;
}

.tb-sync-tab.is-active i {
  color: #4f46e5;
}

.tb-sync-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 20px 24px 24px;
  overflow: hidden;
  background: #f8fafc;
}

.query-layout {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: stretch;
  gap: 20px;
  overflow: hidden;
}

.dept-filter-pane {
  position: relative;
  flex: 0 0 280px;
  width: 280px;
  min-width: 280px;
  flex-shrink: 0;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #2563eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  overflow: visible !important;
  transition: all 0.25s ease;
}

.dept-filter-pane.is-collapsed {
  flex: 0 0 0 !important;
  width: 0 !important;
  min-width: 0 !important;
  max-width: 0 !important;
  padding: 0 !important;
  border: none !important;
  margin: 0 !important;
  overflow: visible !important;
  box-shadow: none !important;
}

.dept-filter-pane.is-collapsed .dept-filter-header,
.dept-filter-pane.is-collapsed .dept-scope-bar {
  display: none !important;
}

.dept-filter-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.dept-filter-header i {
  color: #2563eb;
}

/* 原生 button 必须能收缩，否则撑破左侧栏。最后更新：2026-10-30 */
.dept-scope-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 8px 10px 10px;
  border-bottom: 1px solid #f1f5f9;
  background: #f8fafc;
}

.dept-scope-btn {
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  padding: 6px 4px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  line-height: 1.25;
  cursor: pointer;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: border-color .15s ease, color .15s ease, background .15s ease;
}

.dept-scope-btn:hover {
  border-color: #93c5fd;
  color: #2563eb;
}

.dept-scope-btn.is-active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 600;
}

.dept-filter-pane ::v-deep .department-pane {
  flex: 1;
  height: auto;
  min-height: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dept-filter-pane ::v-deep .department-pane .el-tree {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #94a3b8 #f1f5f9;
}

.dept-filter-pane ::v-deep .department-pane .el-tree-node__content {
  height: auto;
  min-height: 32px;
  padding-top: 4px;
  padding-bottom: 4px;
}

/* DepartmentPane 自定义节点用 .tree-node__label，旧选择器 .el-tree-node__label 已失效。最后更新：2026-10-30 */
.dept-filter-pane ::v-deep .tree-node__label {
  white-space: normal;
  word-break: break-all;
  line-height: 1.4;
  text-overflow: unset;
  overflow: visible;
}

.dept-filter-pane ::v-deep .department-pane .el-tree::-webkit-scrollbar {
  width: 6px;
}

.dept-filter-pane ::v-deep .department-pane .el-tree::-webkit-scrollbar-thumb {
  background: #94a3b8;
  border-radius: 4px;
}

.query-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 4px;
  display: flex;
  flex-direction: column;
  scrollbar-width: thin;
  scrollbar-color: #94a3b8 #f1f5f9;
}

.maint-header-banner {
  flex: 0 0 auto;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
}

.maint-header-banner.is-collapsed {
  padding: 6px 12px;
  margin-bottom: 8px;
  background: #f8fafc;
}

.maint-header-banner:not(.is-collapsed) {
  padding: 10px 14px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
}

.banner-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 0;
  cursor: pointer;
  user-select: none;
  min-height: 28px;
}

.maint-header-banner:not(.is-collapsed) .banner-title-row {
  margin-bottom: 10px;
}

.banner-title-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.banner-collapse-icon {
  flex-shrink: 0;
  font-size: 13px;
  color: #94a3b8;
}

.banner-hint {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 400;
  color: #94a3b8;
}

.banner-icon {
  font-size: 15px;
  color: #2563eb;
}

.panel-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  line-height: 1.3;
}

.panel-desc {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}

.desc-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.desc-badge {
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  flex-shrink: 0;
}

.badge-primary {
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  border: 1px solid rgba(37, 99, 235, 0.2);
}

.badge-warning {
  background: rgba(217, 119, 6, 0.1);
  color: #d97706;
  border: 1px solid rgba(217, 119, 6, 0.2);
}

.status-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  font-size: 13px;
  color: #64748b;
}

.status-chip i {
  color: #94a3b8;
}

.status-chip strong {
  color: #1e293b;
  font-weight: 600;
}

.status-chip.chip-success {
  border-color: rgba(16, 185, 129, 0.3);
  background: rgba(16, 185, 129, 0.06);
}

.status-chip.chip-success i {
  color: #10b981;
}

.status-chip.chip-muted {
  background: #f1f5f9;
}

.notice-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
}

.notice-info {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

.notice-warning {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}

.notice-muted {
  background: #f8fafc;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.sync-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
  flex-shrink: 0;
  min-width: 0;
}
.sync-head-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  min-width: 0;
}
.sync-metrics {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 1 auto;
}
.sync-head .status-strip {
  margin-bottom: 0;
  flex: 1 1 auto;
}
.sync-head-tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}
.sync-head-tip {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #d97706;
}
.sync-head-tip.is-warning {
  color: #b45309;
}
.guide-block + .guide-block {
  margin-top: 14px;
}
.guide-text {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.7;
  color: #334155;
}

/* 手机端：顶栏/操作区换行，布局高度交给全局 query-layout 规则。最后修改：2026-08-31 */
@media (max-width: 768px) {
  .tb-sync-layout {
    height: auto;
    max-height: none;
    overflow: visible;
    border-radius: 0;
  }

  .tb-sync-topbar {
    padding: 12px 12px 0;
  }

  .tb-sync-topbar-title {
    font-size: 15px;
    margin-bottom: 8px;
  }

  .tb-sync-tabs {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 2px;
  }

  .tb-sync-tab {
    padding: 8px 12px;
    font-size: 13px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .tb-sync-main {
    padding: 12px 10px 16px;
    overflow: visible;
    height: auto;
  }

  .query-layout {
    flex-direction: column;
    height: auto;
    overflow: visible;
    gap: 10px;
  }

  .dept-filter-pane {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    max-height: 280px;
    height: auto;
  }

  .query-main {
    height: auto;
    overflow: visible;
    padding-right: 0;
  }

  .log-card {
    flex: none;
    height: 420px;
    min-height: 350px;
  }

  .status-strip {
    flex-wrap: wrap;
    gap: 8px;
  }

  .action-row {
    flex-wrap: wrap;
    gap: 8px;
  }

  .action-row .el-button {
    margin-left: 0 !important;
  }

  .stats-detail-search-input {
    max-width: 100%;
  }
}

.stat-card {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  white-space: nowrap;
  flex-shrink: 0;
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
}

.stat-value {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
}

.stat-value .unit {
  font-size: 12px;
  margin-left: 4px;
  font-weight: 500;
}

.stat-icon-bg {
  display: none;
}

.stat-blue {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #1e40af;
}

.stat-emerald {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  color: #166534;
}

.stat-orange {
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  color: #c2410c;
}

.stat-card-clickable {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.stat-card-clickable:hover {
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.stats-detail-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #64748b;
}

.stats-detail-meta i {
  margin-right: 4px;
  color: #94a3b8;
}

.stats-detail-tip {
  color: #d97706;
}

.stats-detail-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.stats-detail-search-input {
  flex: 1;
  max-width: 360px;
}

.stats-detail-table-wrap {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.stats-detail-table-wrap ::v-deep .el-table {
  width: 100%;
  border: none;
}

.stats-detail-table-wrap ::v-deep .el-table::before,
.stats-detail-table-wrap ::v-deep .el-table::after {
  display: none;
}

/* 勿强制 header/body 同宽、勿隐藏 gutter，否则有纵向滚动时竖线错位。最后更新：2026-10-21 */
.stats-detail-table-wrap ::v-deep .el-table__body tr:last-child td {
  border-bottom: none;
}

.stats-detail-table ::v-deep .el-table__body td,
.stats-detail-table ::v-deep .el-table__header th {
  padding: 8px 10px;
}

.stats-detail-table ::v-deep .el-table__body td .cell,
.stats-detail-table ::v-deep .el-table__header th .cell {
  padding-left: 0;
  padding-right: 0;
}

.stats-detail-table ::v-deep .detail-id-cell-ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stats-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #d97706;
  margin-bottom: 8px;
  padding: 0 2px;
  flex-shrink: 0;
}

.step-card {
  border-radius: 10px !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02) !important;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.step-card:not(.log-card) ::v-deep .el-card__header {
  padding: 6px 12px;
}

.step-card:not(.log-card) ::v-deep .el-card__body {
  padding: 8px 12px;
}

.branch-card {
  border-left: 4px solid #2563eb !important;
}

.branch-card-teal {
  border-left: 4px solid #0d9488 !important;
}

.card-header-styled {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
}

.card-header-styled i {
  color: #2563eb;
}

.action-header {
  justify-content: space-between;
}

.action-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.action-hint {
  font-size: 12px;
  color: #94a3b8;
}

.action-hint-ok {
  color: #10b981;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.action-hint-secondary {
  margin-top: 8px;
  line-height: 1.45;
  font-size: 12px;
}

.log-card {
  /* 吃满剩余高度；不够时不低于 350，由 query-main 整体滚动。最后修改：2026-08-31 */
  flex: 1 1 auto;
  min-height: 350px;
  height: 0;
  margin-bottom: 0 !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.log-card ::v-deep .el-card__header {
  flex-shrink: 0;
}

.log-card ::v-deep .el-card__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 8px;
}

.stats-detail-op-cell {
  width: 100%;
}

.stats-detail-op-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 2px;
  white-space: nowrap;
}

.stats-detail-table .link-btn {
  margin: 0 !important;
  padding: 0 4px !important;
  color: #4f46e5 !important;
  font-weight: 500;
}

.stats-detail-table .link-btn.is-disabled {
  color: #c0c4cc !important;
  cursor: not-allowed;
}

.stats-detail-table .link-btn-warning {
  color: #d97706 !important;
}

.stats-detail-table .link-btn-danger {
  color: #ef4444 !important;
}
</style>

<style>
/* 统计明细弹窗：仅表格内部滚动（overflow 由 dialog-theme 自管名单控制）。最后更新：2026-10-21 */
.el-dialog.tb-sync-detail-dialog.tb-sync-stats-detail-dialog > .el-dialog__body {
  overflow: hidden !important;
}
</style>
